import { useState } from 'react';
import { Link } from 'react-router';
import {
  Button,
  EmptyState,
  InlineError,
  Input,
  Panel,
  Select,
  Skeleton,
} from '../../components/ui';
import { PageHeader } from '../../layouts/Shells';
import { useAuth } from '../auth/AuthProvider';
import { useSaveProfileSettings } from '../settings/hooks';
import { useSaveShiftNote, useShiftActivity } from './queries';
import { digestDoc, digestNoteTitle, digestText, groupShift } from './shiftDigest';
import { currentShiftDay, hourLabel, shiftLabel, shiftWindow } from './shiftWindow';
import './myShift.css';

/**
 * My Shift: what you did, while you were here.
 *
 * Hanneza's brief, 4 Sep — an eight-hour shift inside the Hub leaves nothing to
 * write an EOD from, because the record of it is spread across six tables she
 * cannot open. Everything on this page already existed in the database; the
 * only new thing is being able to read it.
 *
 * ── Why a button and not a scheduled write ─────────────────────────────────
 *
 * The brief asked for the digest to appear in My Notes on its own. That needs a
 * scheduler, every person's shift-end in every zone, idempotency so a retry
 * does not write twice, and a rule for what happens to a note she has already
 * edited. None of it changes what she ends up holding. Save to My Notes gets
 * her the same note the moment she wants it — and answers both of her open
 * questions for free: past shifts are a date picker over permanent logs, and
 * "one running note or one per shift" stops being a decision because she
 * presses the button when she wants an entry.
 * docs/shift-digest-design-note.md has the rest of the argument.
 */

const HOURS = Array.from({ length: 24 }, (_, hour) => ({
  value: String(hour),
  // 12-hour, because the shift is stated the way people say it out loud:
  // "I start at 8pm", never "I start at 20:00". hourLabel is zone-free on
  // purpose; see its comment for the bug an unpinned formatter caused.
  label: hourLabel(hour),
}));

const LENGTHS = [4, 6, 8, 9, 10, 12].map((hours) => ({
  value: String(hours),
  label: `${String(hours)} hours`,
}));

function ShiftSettings({
  startHour,
  lengthHours,
  timeZone,
}: {
  startHour: number;
  lengthHours: number;
  timeZone: string;
}) {
  const save = useSaveProfileSettings();
  const [problem, setProblem] = useState('');

  function change(patch: { shiftStartHour?: number; shiftLengthHours?: number }) {
    setProblem('');
    save.mutate(patch, { onError: (cause: Error) => setProblem(cause.message) });
  }

  return (
    <div className="my-shift-settings">
      <Select
        label="Shift starts"
        value={String(startHour)}
        options={HOURS}
        onChange={(event) => change({ shiftStartHour: Number(event.target.value) })}
      />
      <Select
        label="Runs for"
        value={String(lengthHours)}
        options={LENGTHS}
        onChange={(event) => change({ shiftLengthHours: Number(event.target.value) })}
      />
      <p className="my-shift-zone">
        Times shown in {timeZone}. <Link to="/settings">Change your time zone</Link>
      </p>
      {problem === '' ? null : <InlineError>{problem}</InlineError>}
    </div>
  );
}

export function MyShiftPage() {
  const { access } = useAuth();
  const profile = access.status === 'approved' ? access.profile : null;

  const settings = {
    startHour: profile?.shift_start_hour ?? 9,
    lengthHours: profile?.shift_length_hours ?? 8,
    timeZone: profile?.time_zone ?? 'UTC',
  };

  const [day, setDay] = useState(() => currentShiftDay(settings));
  const window = shiftWindow(day, settings);
  const activity = useShiftActivity(window === null ? null : { from: window.from, to: window.to });
  const saveNote = useSaveShiftNote();
  const [copied, setCopied] = useState(false);
  const [savedNoteId, setSavedNoteId] = useState<string | null>(null);
  const [problem, setProblem] = useState('');

  const zone = window?.fellBackToUtc === true ? 'UTC' : settings.timeZone;
  const groups = groupShift(activity.data ?? [], zone);
  const title = window === null ? day : shiftLabel(window, settings.timeZone);
  const text = digestText(title, groups);
  const total = groups.reduce((sum, group) => sum + group.lines.length, 0);

  function copy() {
    setProblem('');
    navigator.clipboard.writeText(text).then(
      () => {
        setCopied(true);
        // Long enough to read, short enough that it is gone before she wonders
        // whether it stuck.
        globalThis.setTimeout(() => setCopied(false), 2500);
      },
      () => setProblem('The browser would not let the Hub write to the clipboard.'),
    );
  }

  function save() {
    setProblem('');
    setSavedNoteId(null);
    saveNote.mutate(
      { title: digestNoteTitle(title), doc: digestDoc(title, groups) },
      {
        onSuccess: (id: string) => setSavedNoteId(id),
        onError: (cause: Error) => setProblem(cause.message),
      },
    );
  }

  return (
    <>
      <PageHeader
        title="My Shift"
        topbar
        description="What you did in the Hub during one shift, ready to paste into your EOD."
      />

      <Panel>
        <div className="my-shift-controls">
          <Input
            label="Shift starting"
            type="date"
            value={day}
            onChange={(event) => {
              setDay(event.target.value);
              setSavedNoteId(null);
            }}
            hint="A shift is filed under the day it started, even when it ends the next morning."
          />
          <ShiftSettings
            startHour={settings.startHour}
            lengthHours={settings.lengthHours}
            timeZone={settings.timeZone}
          />
        </div>
      </Panel>

      <Panel>
        <div className="my-shift-head">
          <h2 className="my-shift-title">{title}</h2>
          <div className="my-shift-actions">
            <Button size="sm" variant="secondary" onClick={copy} disabled={activity.isPending}>
              {copied ? 'Copied' : 'Copy'}
            </Button>
            <Button size="sm" onClick={save} disabled={activity.isPending || saveNote.isPending}>
              {saveNote.isPending ? 'Saving…' : 'Save to My Notes'}
            </Button>
          </div>
        </div>

        {window?.fellBackToUtc === true ? (
          <InlineError>
            Your time zone is not one this browser recognises, so these times are UTC. Set it in
            Settings.
          </InlineError>
        ) : null}
        {problem === '' ? null : <InlineError>{problem}</InlineError>}
        {savedNoteId === null ? null : (
          <p className="my-shift-saved">
            Saved. <Link to="/notes">Open My Notes</Link>
          </p>
        )}

        {activity.isPending ? (
          <Skeleton />
        ) : activity.isError ? (
          <InlineError>{activity.error.message}</InlineError>
        ) : total === 0 ? (
          <EmptyState
            title="Nothing recorded for this shift"
            description="Everything you do in the Hub shows up here as you do it: tasks, deals, leads, emails, orders, files, reviews, comments, projects, posts, leave and more."
          />
        ) : (
          <div className="my-shift-groups">
            {groups.map((group) => (
              <section key={group.category} className="my-shift-group">
                <h3 className="my-shift-group-title">
                  {group.category}
                  <span className="my-shift-count">{group.lines.length}</span>
                </h3>
                <ul className="my-shift-lines">
                  {group.lines.map((line, index) => (
                    <li key={`${group.category}-${String(index)}`} className="my-shift-line">
                      <span className="my-shift-time">{line.time}</span>
                      <span className="my-shift-text">{line.text}</span>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </Panel>
    </>
  );
}
